// src/components/dashboard/cases/PatientLabel.js (complete file)
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import DatePicker from 'react-datepicker';
import axios from 'axios';
import { FaChevronDown, FaChevronUp, FaTimes, FaEye, FaPlus } from 'react-icons/fa';
import 'react-datepicker/dist/react-datepicker.css';

// Bind modal to app element for accessibility
Modal.setAppElement('#root');

const PatientLabel = ({ patientName, caseId, onUpdate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [patientData, setPatientData] = useState({
    first_name: '',
    last_name: '',
    patientChart: '',
    birth_date: null,
  });
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    patientChart: '',
    birth_date: null,
  });
  const [accountData, setAccountData] = useState({});
  const [accountName, setAccountName] = useState('');
  const [displayName, setDisplayName] = useState(patientName || 'No Patient Name');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { caseId: urlCaseId } = useParams();
  const effectiveCaseId = caseId || urlCaseId;

  // Fetch case details
  useEffect(() => {
    const fetchCaseDetails = async () => {
      try {
        const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/cases/cases/${effectiveCaseId}`);
        const caseData = response.data;
        setPatientData({
          first_name: caseData.first_name || '',
          last_name: caseData.last_name || '',
          patientChart: caseData.patientChart || '',
          birth_date: caseData.birth_date ? new Date(caseData.birth_date) : null,
        });

        if (caseData.account_holder) {
          try {
            const accountResponse = await axios.get(`https://hms.backend.hiragroupcloud.com/api/accounts/accounts/${caseData.account_holder}`);
            const acc = accountResponse.data;
            setAccountData(acc);
            setAccountName(
              acc.name ||
              `${acc.first_name || ''} ${acc.last_name || ''}`.trim() ||
              caseData.account_holder ||
              'Unknown Account'
            );
          } catch (accErr) {
            console.error('Error fetching account details:', accErr);
            setAccountName(caseData.account_holder || 'Unknown Account');
          }
        }
      } catch (err) {
        setError('Failed to fetch patient details.');
        console.error('Error fetching case:', err);
      }
    };

    if (effectiveCaseId) {
      fetchCaseDetails();
    }
  }, [effectiveCaseId]);

  // Update display name when patientData or accountName changes
  useEffect(() => {
    const computedName = `${patientData.first_name || ''} ${patientData.last_name || ''}`.trim() || accountName || 'No Patient Name';
    setDisplayName(computedName);
  }, [patientData, accountName]);

  // Open modal and prefill if necessary
  const openModal = () => {
    setIsExpanded(false);
    let prefilledData = { ...patientData };
    if (!patientData.first_name && !patientData.last_name && accountData) {
      prefilledData.first_name = accountData.first_name || (accountData.name ? accountData.name.split(' ')[0] : '') || '';
      prefilledData.last_name = accountData.last_name || (accountData.name ? accountData.name.split(' ').slice(1).join(' ') : '') || '';
    }
    setEditFormData(prefilledData);
    setIsModalOpen(true);
    setError('');
    setSuccess('');
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setError('');
    setSuccess('');
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle date change
  const handleDateChange = (date) => {
    setEditFormData((prev) => ({ ...prev, birth_date: date }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!editFormData.first_name || !editFormData.last_name) {
      setError('First Name and Last Name are required.');
      setLoading(false);
      return;
    }

    try {
      const updatedData = {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        patientChart: editFormData.patientChart,
        birth_date: editFormData.birth_date ? editFormData.birth_date.toISOString() : null,
      };

      const response = await axios.put(
        `https://hms.backend.hiragroupcloud.com/api/cases/cases/${effectiveCaseId}/patient`,
        updatedData
      );
      setSuccess('Patient details updated successfully!');
      setPatientData({
        first_name: updatedData.first_name,
        last_name: updatedData.last_name,
        patientChart: updatedData.patientChart,
        birth_date: editFormData.birth_date, // Keep as Date object
      });
      if (onUpdate) {
        onUpdate({ ...updatedData, caseNumber: effectiveCaseId });
      }
      setTimeout(() => {
        closeModal();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update patient details.');
      console.error('Error updating patient details:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle eye button click to store patient name in sessionStorage and navigate
  const handleEyeButtonClick = (e) => {
    e.stopPropagation();

    if (displayName && displayName !== 'No Patient Name') {
      sessionStorage.setItem('patientFullName', displayName);
      
      setSuccess('Patient name saved!');
      setTimeout(() => setSuccess(''), 2000);
    }

    // Navigate to patient records
    navigate(`/patientrecords/${effectiveCaseId}`);
  };

  return (
    <div style={{ padding: '10px', maxWidth: '500px' }}>
      <p style={{ fontWeight: 600, fontSize: '16px', color: '#333', marginBottom: '5px' }}>
        Patient:
      </p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Link
          to={`/patientrecords/${effectiveCaseId}`}
          style={{
            textDecoration: 'none',
            color: '#004aad',
            fontSize: '18px',
            fontWeight: 500,
            transition: 'color 0.2s',
          }}
          onMouseOver={(e) => (e.target.style.color = '#002a6d')}
          onMouseOut={(e) => (e.target.style.color = '#004aad')}
          title="View Patient Record"
        >
          <h4 style={{ margin: 0 }}>{displayName}</h4>
        </Link>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleEyeButtonClick}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '6px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = '#218838')}
            onMouseOut={(e) => (e.target.style.backgroundColor = '#28a745')}
            title="View Patient and store name in session"
          >
            <FaEye size={14} />
          </button>
          <Link
            to={`/accountDetails/${effectiveCaseId}`}
            style={{
              backgroundColor: '#ffc107',
              color: 'white',
              border: 'none',
              padding: '6px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = '#e0a800')}
            onMouseOut={(e) => (e.target.style.backgroundColor = '#ffc107')}
            title="Create New Case"
          >
            <FaPlus size={14} />
          </Link>
          <button
            style={{
              backgroundColor: '#004aad',
              color: 'white',
              border: 'none',
              padding: '6px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => (e.target.style.backgroundColor = '#002a6d')}
            onMouseOut={(e) => (e.target.style.backgroundColor = '#004aad')}
            onClick={openModal}
            title="Edit Patient Details"
          >
            {isExpanded ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Success message for eye button click */}
      {success && !isModalOpen && (
        <p style={{
          color: '#28a745',
          fontSize: '12px',
          marginTop: '5px',
          animation: 'fadeInOut 2s ease-in-out'
        }}>
          {success}
        </p>
      )}

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Edit Patient Details"
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          content: {
            background: 'white',
            borderRadius: '8px',
            padding: '15px',
            width: '90%',
            maxWidth: '350px',
            height: '400px',
            margin: 'auto',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: 'none',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
          }}
        >
          <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#333', margin: 0 }}>
            Edit Patient Details
          </h2>
          <button
            onClick={closeModal}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#888',
              fontSize: '16px',
            }}
            onMouseOver={(e) => (e.target.style.color = '#555')}
            onMouseOut={(e) => (e.target.style.color = '#888')}
          >
            <FaTimes size={16} />
          </button>
        </div>
        <form
          onSubmit={handleSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            flexGrow: 1,
          }}
        >
          {error && (
            <p style={{ color: '#dc3545', fontSize: '12px', marginBottom: '5px' }}>
              {error}
            </p>
          )}
          {success && (
            <p style={{ color: '#28a745', fontSize: '12px', marginBottom: '5px' }}>
              {success}
            </p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <label
              style={{ fontWeight: 500, color: '#444', marginBottom: '4px', fontSize: '14px' }}
            >
              First Name <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              name="first_name"
              value={editFormData.first_name}
              onChange={handleInputChange}
              placeholder="Enter first name"
              required
              style={{
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                width: '100%',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => (e.target.style.borderColor = '#004aad')}
              onBlur={(e) => (e.target.style.borderColor = '#ddd')}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <label
              style={{ fontWeight: 500, color: '#444', marginBottom: '4px', fontSize: '14px' }}
            >
              Last Name <span style={{ color: '#dc3545' }}>*</span>
            </label>
            <input
              type="text"
              name="last_name"
              value={editFormData.last_name}
              onChange={handleInputChange}
              placeholder="Enter last name"
              required
              style={{
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                width: '100%',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => (e.target.style.borderColor = '#004aad')}
              onBlur={(e) => (e.target.style.borderColor = '#ddd')}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <label
              style={{ fontWeight: 500, color: '#444', marginBottom: '4px', fontSize: '14px' }}
            >
              Patient Chart
            </label>
            <input
              type="text"
              name="patientChart"
              value={editFormData.patientChart}
              onChange={handleInputChange}
              placeholder="Enter patient chart"
              style={{
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                width: '100%',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => (e.target.style.borderColor = '#004aad')}
              onBlur={(e) => (e.target.style.borderColor = '#ddd')}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <label
              style={{ fontWeight: 500, color: '#444', marginBottom: '4px', fontSize: '14px' }}
            >
              Birth Date
            </label>
            <DatePicker
              selected={editFormData.birth_date}
              onChange={handleDateChange}
              dateFormat="dd/MM/yyyy"
              placeholderText="Select birth date"
              style={{
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            style={{
              backgroundColor: loading ? '#cccccc' : '#004aad',
              color: 'white',
              padding: '8px',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              transition: 'background-color 0.2s',
              marginTop: 'auto',
            }}
            onMouseOver={(e) => !loading && (e.target.style.backgroundColor = '#002a6d')}
            onMouseOut={(e) => !loading && (e.target.style.backgroundColor = '#004aad')}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </form>
      </Modal>
    </div>
  );
};

export default PatientLabel;